import React, { useState } from 'react';
import { Star, Send, MessageSquare } from 'lucide-react';

function StarRating({ value, onChange, size = 24, readonly = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(star => {
        const filled = star <= (hover || value);
        return (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onChange?.(star)}
            onMouseEnter={() => !readonly && setHover(star)}
            onMouseLeave={() => !readonly && setHover(0)}
            className={`transition-all duration-150 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            data-testid={`star-${star}`}
          >
            <Star
              size={size}
              fill={filled ? '#A8752B' : 'none'}
              stroke={filled ? '#A8752B' : '#D1D5DB'}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
}

export function StarDisplay({ rating, count, size = 14 }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-px">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            size={size}
            fill={star <= Math.round(rating) ? '#A8752B' : 'none'}
            stroke={star <= Math.round(rating) ? '#A8752B' : '#D1D5DB'}
            strokeWidth={1.5}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-manrique-grey">
        {rating > 0 ? rating.toFixed(1) : '-'}
      </span>
      {count !== undefined && (
        <span className="text-xs text-manrique-muted">({count})</span>
      )}
    </div>
  );
}

export default function ReviewForm({ reservation, api, onReviewSubmitted }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!reservation.review);

  if (submitted || reservation.review) {
    const rev = reservation.review || {};
    return (
      <div className="flex items-center gap-3 mt-2 p-3 rounded-xl bg-[#FBF8F3]" data-testid={`review-display-${reservation.id}`}>
        <StarRating value={rev.rating || rating} readonly size={16} />
        {(rev.comment || comment) && (
          <p className="text-xs text-manrique-grey italic truncate flex-1">"{rev.comment || comment}"</p>
        )}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 mt-2 text-xs font-medium text-[#48293A] hover:underline"
        data-testid={`review-open-${reservation.id}`}
      >
        <MessageSquare size={13} /> Valorar este taller
      </button>
    );
  }

  const handleSubmit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await api.post('/reviews', {
        reservation_id: reservation.id,
        workshop_id: reservation.workshop_id,
        rating,
        comment,
      });
      setSubmitted(true);
      setOpen(false);
      if (onReviewSubmitted) onReviewSubmitted();
    } catch (err) { console.error('Review submit error:', err); }
    setSubmitting(false);
  };

  return (
    <div className="mt-3 p-4 rounded-2xl border border-[#48293A]/10 bg-[#48293A]/[0.02] space-y-3 animate-fade-in" data-testid={`review-form-${reservation.id}`}>
      <p className="text-sm font-medium text-manrique-black">Como fue la experiencia?</p>
      <StarRating value={rating} onChange={setRating} size={28} />
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Escribe un comentario (opcional)..."
        className="w-full h-20 bg-white border border-black/10 rounded-xl px-4 py-3 text-sm text-manrique-black placeholder:text-manrique-muted focus:outline-none focus:ring-2 focus:ring-[#48293A] resize-none"
        data-testid={`review-comment-${reservation.id}`}
      />
      <div className="flex items-center justify-between">
        <button
          onClick={() => setOpen(false)}
          className="text-xs text-manrique-muted hover:text-manrique-grey"
          data-testid={`review-cancel-${reservation.id}`}
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={rating === 0 || submitting}
          className="btn-manrique text-sm py-2 px-5 flex items-center gap-1.5 disabled:opacity-40"
          data-testid={`review-submit-${reservation.id}`}
        >
          <Send size={13} /> {submitting ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </div>
  );
}
